import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  IconButton,
  Popover,
  TextField,
  Tile,
  TileGroup,
  Typography
} from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { filterData } from '../helpers';

export const PopoverFeedback = (
  {
    open,
    anchorEl,
    reasons,
    onClose,
    onSubmit,
    buttonsBaseId,
    title,
    closeButtonAriaLabel,
    textAreaPlaceholder,
    submitCTA,
    placement,
    disableEnforceFocus
  }
) => {
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const dataToSubmit = useMemo(() => (filterData({
    feedbackReason,
    feedbackComment
  })), [feedbackReason, feedbackComment]);

  const handleSelectReason = (__, selected) => {
    setFeedbackReason(selected);
    setCanSubmit(true);
  };

  const populateReasonOptions = () => {
    return reasons?.map(
      (item) => <Tile value={item} key={item}>{item}</Tile>);
  };

  const handleChange = (event) => setFeedbackComment(event.target.value);

  const handleSubmit = () => onSubmit(dataToSubmit);

  return isMobile ? (
    <Drawer open={open} onClose={onClose} anchor="bottom">
      <DrawerHeader title={title} onClose={onClose} />
      <DrawerBody>
        <section className="sui-flex sui-flex-col sui-gap-2 sui-mb-4">
          <TileGroup
            value={feedbackReason}
            onChange={handleSelectReason}
            aria-label="Reason options"
          >
            {populateReasonOptions()}
          </TileGroup>
          <TextField placeholder={textAreaPlaceholder} onChange={handleChange} multiline minRows={3} fullWidth />
        </section>
      </DrawerBody>
      <DrawerFooter>
        <Button
          id={`${buttonsBaseId}_user-feedback_submit`}
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="primary"
          fullWidth
        >
          {submitCTA}
        </Button>
      </DrawerFooter>
    </Drawer>
  ) : (
    <Popover
      open={open}
      anchorEl={anchorEl?.current || anchorEl}
      placement={placement}
      disableEnforceFocus={disableEnforceFocus}
    >
      <header className="sui-flex sui-justify-between sui-items-center sui-gap-2 sui-mb-4">
        <Typography variant="body-base" weight="bold">{title}</Typography>{' '}<IconButton
          icon={Close}
          size="small"
          onClick={onClose}
          aria-label={closeButtonAriaLabel}
        />
      </header>
      <section className="sui-flex sui-flex-col sui-gap-2 sui-mb-4">
        <TileGroup
          value={feedbackReason}
          onChange={handleSelectReason}
          aria-label="Reason options"
        >
          {populateReasonOptions()}
        </TileGroup>
        <TextField placeholder={textAreaPlaceholder} onChange={handleChange} multiline minRows={3} fullWidth />
      </section>
      <footer className="sui-w-full">
        <Button
          id={`${buttonsBaseId}_user-feedback_submit`}
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="primary"
          fullWidth
        >
          {submitCTA}
        </Button>
      </footer>
    </Popover>
  );
};

PopoverFeedback.displayName = 'PopoverFeedback';

PopoverFeedback.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.instanceOf(React.ElementType) }),
    PropTypes.func, PropTypes.oneOf([null])
  ]),
  reasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  buttonsBaseId: PropTypes.string.isRequired,
  title: PropTypes.string,
  closeButtonAriaLabel: PropTypes.string,
  textAreaPlaceholder: PropTypes.string,
  submitCTA: PropTypes.string,
  placement: PropTypes.string.isRequired,
  disableEnforceFocus: PropTypes.bool
};

PopoverFeedback.defaultProps = {
  anchorEl: null,
  title: 'Why did you select this feedback?',
  closeButtonAriaLabel: 'Close',
  textAreaPlaceholder: 'Add details here',
  submitCTA: 'Submit',
  disableEnforceFocus: false
};
